import { Box, Stack, Typography } from '@mui/material';
import { messenger } from '@side/next-zendesk-sdk';
import { AppLayoutWithNav, useIsMobile } from '@side/pantry';
import NextLink from 'next/link';
import type { PropsWithChildren } from 'react';
import { AccountSettingsSideNavigation } from '@/components/AccountSettingsSideNavigation';
import { NavigationAccordion } from '@/components/AccountSettingsSideNavigation/NavigationAccordion';
import { GQLAppFeature } from '@/generated/graph-api';
import { useHasAccessToCreateTeamMember } from '@/hooks/use-has-access-to-create-team-member-feature';
import { useHasAccessToFeature } from '@/hooks/use-has-access-to-feature';
import { useUserInformationForAppHeader } from '@/hooks/use-user-information-for-app-header';
import { PlatformFeeAlert } from '@/views/platform-fee';
import { useAppFlags } from '$launchdarkly/useAppFlags';
import { Download1099Alert } from '$tax-documents';

/**
 * Layout wrapper component for agent-facing pages that provides consistent app structure.
 */
export function AgentPageLayout({
  children,
  hideSidebar = false,
}: PropsWithChildren<{ hideSidebar?: boolean }>) {
  const user = useUserInformationForAppHeader();
  const hasAccessToLeases = useHasAccessToFeature(GQLAppFeature.LEASES);
  const hasAccessToSideListings = useHasAccessToFeature(
    GQLAppFeature.COMMUNITY_LISTINGS,
  );
  const isMobile = useIsMobile('md');
  const hasAccessToCreateTeamMember = useHasAccessToCreateTeamMember();
  const { txmSideXSideAttendees, txmSideXSidePage } = useAppFlags();
  const sideXSideUrl = txmSideXSideAttendees
    ? '/txm/side-x-side'
    : 'https://sidexside.side.com/';

  return (
    <AppLayoutWithNav
      navProps={{
        homePath: '/txm',
        basePath: '/identity',
        user,
        showLeasesLink: hasAccessToLeases,
        showSideListingsLink: hasAccessToSideListings,
        showCreateTeamMemberLink: hasAccessToCreateTeamMember,
        sideXSideHref: txmSideXSidePage ? sideXSideUrl : undefined,
        LinkComponent: NextLink,
        onChat: messenger.open,
        mobileHeaderTitle: (
          <Typography variant="subtitle2">{`${user?.firstName}'s Account`}</Typography>
        ),
        mobileHeaderContent: <NavigationAccordion />,
      }}
    >
      <Stack
        direction="row"
        sx={{ height: '100%', maxHeight: '100dvh', overflow: 'hidden' }}
      >
        {!isMobile && !hideSidebar && <AccountSettingsSideNavigation />}

        <main
          style={{
            height: '100%',
            width: '100%',
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              width: '100%',
              zIndex: '1000',
            }}
          >
            <PlatformFeeAlert />
            <Download1099Alert />
          </Box>
          {children}
        </main>
      </Stack>
    </AppLayoutWithNav>
  );
}
